const TIME_ZONE = 'America/Chicago';
const CUTOFF_HOUR = 14;
const MS_PER_MINUTE = 60 * 1000;
const HOURS_PLACEHOLDER = '__HOURS__';
const MINUTES_PLACEHOLDER = '__MINUTES__';
const WEEKEND_DAYS = new Set(['Sat', 'Sun']);
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SUNDAY = 0;
const MONDAY = 1;
const THURSDAY = 4;
const SATURDAY = 6;

/** @type {Map<number, Set<string>>} */
const federalHolidayCache = new Map();

/**
 * @typedef {Object} ChicagoDateTime
 * @property {number} year
 * @property {number} month
 * @property {number} day
 * @property {string} weekday
 * @property {number} hour
 * @property {number} minute
 * @property {number} second
 */

/**
 * @typedef {Object} ShippingCutoffLabels
 * @property {string} [shipsToday] Template with __HOURS__ and __MINUTES__
 * @property {string} [shipsTomorrow]
 * @property {string} [nextBusinessDay]
 */

/**
 * @typedef {Object} CutoffCountdown
 * @property {number} hours
 * @property {number} minutes
 * @property {Date} nextTickAt
 */

/**
 * @param {Date} [date]
 * @returns {ChicagoDateTime}
 */
export const getChicagoDateTime = (date = new Date()) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  /** @type {Record<string, string>} */
  const values = {};

  for (const part of formatter.formatToParts(date)) {
    if (part.type === 'literal') continue;
    values[part.type] = part.value;
  }

  let hour = Number(values.hour);
  if (hour === 24) hour = 0;

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    weekday: values.weekday,
    hour,
    minute: Number(values.minute),
    second: Number(values.second),
  };
};

/**
 * @param {{ year: number, month: number, day: number, hour?: number, minute?: number, second?: number }} wall
 * @returns {Date}
 */
const chicagoWallTimeToDate = (wall) => {
  const hour = wall.hour ?? 0;
  const minute = wall.minute ?? 0;
  const second = wall.second ?? 0;
  const utcGuess = Date.UTC(wall.year, wall.month - 1, wall.day, hour, minute, second);
  const asChicago = getChicagoDateTime(new Date(utcGuess));
  const asUtc = Date.UTC(
    asChicago.year,
    asChicago.month - 1,
    asChicago.day,
    asChicago.hour,
    asChicago.minute,
    asChicago.second
  );

  return new Date(utcGuess - (asUtc - utcGuess));
};

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @returns {string}
 */
const dateKey = (year, month, day) => `${year}-${month}-${day}`;

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @param {number} delta
 * @returns {{ year: number, month: number, day: number }}
 */
const shiftUtcDate = (year, month, day, delta) => {
  const shifted = new Date(Date.UTC(year, month - 1, day + delta));

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
};

/**
 * @param {number} year
 * @param {number} month
 * @param {number} weekday Sunday = 0
 * @param {number} nth 1-based; -1 is the last occurrence in the month
 * @returns {{ year: number, month: number, day: number }}
 */
const nthWeekdayOfMonth = (year, month, weekday, nth) => {
  if (nth < 0) {
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const lastWeekday = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay();
    const day = lastDay - ((lastWeekday - weekday + 7) % 7);

    return { year, month, day };
  }

  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const day = 1 + ((weekday - firstWeekday + 7) % 7) + (nth - 1) * 7;

  return { year, month, day };
};

/**
 * Weekend federal holidays are observed on Friday or Monday.
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @returns {{ year: number, month: number, day: number }}
 */
const observedHoliday = (year, month, day) => {
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

  if (weekday === SATURDAY) return shiftUtcDate(year, month, day, -1);
  if (weekday === SUNDAY) return shiftUtcDate(year, month, day, 1);

  return { year, month, day };
};

/**
 * U.S. federal holidays for a calendar year, including observed dates.
 * Inauguration Day is omitted (DC-only; 3PLs still ship).
 * @param {number} year
 * @returns {Set<string>}
 */
const federalHolidaysForYear = (year) => {
  const cached = federalHolidayCache.get(year);
  if (cached) return cached;

  const holidays = new Set();
  const addDate = (parts) => {
    holidays.add(dateKey(parts.year, parts.month, parts.day));
  };

  addDate(observedHoliday(year, 1, 1));
  addDate(nthWeekdayOfMonth(year, 1, MONDAY, 3));
  addDate(nthWeekdayOfMonth(year, 2, MONDAY, 3));
  addDate(nthWeekdayOfMonth(year, 5, MONDAY, -1));
  addDate(observedHoliday(year, 6, 19));
  addDate(observedHoliday(year, 7, 4));
  addDate(nthWeekdayOfMonth(year, 9, MONDAY, 1));
  addDate(nthWeekdayOfMonth(year, 10, MONDAY, 2));
  addDate(observedHoliday(year, 11, 11));
  addDate(nthWeekdayOfMonth(year, 11, THURSDAY, 4));
  addDate(observedHoliday(year, 12, 25));

  federalHolidayCache.set(year, holidays);
  return holidays;
};

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @returns {boolean}
 */
const isFederalHoliday = (year, month, day) => {
  const key = dateKey(year, month, day);
  if (federalHolidaysForYear(year).has(key)) return true;

  if (month === 12) return federalHolidaysForYear(year + 1).has(key);

  return false;
};

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @returns {boolean}
 */
const isBusinessDay = (year, month, day) => {
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  if (WEEKEND_DAYS.has(weekday)) return false;

  return !isFederalHoliday(year, month, day);
};

/**
 * Calendar tomorrow in Chicago. Same as "the next business day is tomorrow".
 * @param {ChicagoDateTime} chicago
 * @returns {boolean}
 */
const isTomorrowBusinessDay = (chicago) => {
  const tomorrow = shiftUtcDate(chicago.year, chicago.month, chicago.day, 1);

  return isBusinessDay(tomorrow.year, tomorrow.month, tomorrow.day);
};

/**
 * @param {ChicagoDateTime} chicago
 * @returns {Date}
 */
const getTodayCutoffAt = (chicago) =>
  chicagoWallTimeToDate({
    year: chicago.year,
    month: chicago.month,
    day: chicago.day,
    hour: CUTOFF_HOUR,
  });

/**
 * @param {ChicagoDateTime} chicago
 * @returns {boolean}
 */
export const canShipToday = (chicago) => {
  if (!isBusinessDay(chicago.year, chicago.month, chicago.day)) return false;
  return chicago.hour < CUTOFF_HOUR;
};

/**
 * Remaining time until today's 2pm CT cutoff, rounded up to the next minute.
 * @param {Date} [now]
 * @returns {CutoffCountdown | null}
 */
export const getTimeUntilCutoff = (now = new Date()) => {
  const chicago = getChicagoDateTime(now);
  if (!canShipToday(chicago)) return null;

  const cutoff = getTodayCutoffAt(chicago);
  const remainingMs = cutoff.getTime() - now.getTime();
  if (remainingMs <= 0) return null;

  const totalMinutes = Math.max(1, Math.ceil(remainingMs / MS_PER_MINUTE));

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    nextTickAt: new Date(cutoff.getTime() - (totalMinutes - 1) * MS_PER_MINUTE),
  };
};

/**
 * @param {string} template
 * @param {number} hours
 * @param {number} minutes
 * @returns {string}
 */
const applyCountdownTemplate = (template, hours, minutes) => {
  const hoursValue = String(hours);
  const minutesValue = String(minutes);

  return template
    .replaceAll(HOURS_PLACEHOLDER, hoursValue)
    .replaceAll(MINUTES_PLACEHOLDER, minutesValue)
    .replaceAll('{{ hours }}', hoursValue)
    .replaceAll('{{ minutes }}', minutesValue);
};

/**
 * @param {ChicagoDateTime} chicago
 * @returns {Date}
 */
export const getNextUpdateAt = (chicago) => {
  if (canShipToday(chicago)) {
    return getTodayCutoffAt(chicago);
  }

  for (let offset = 1; offset <= 14; offset++) {
    const next = new Date(Date.UTC(chicago.year, chicago.month - 1, chicago.day + offset));
    const year = next.getUTCFullYear();
    const month = next.getUTCMonth() + 1;
    const day = next.getUTCDate();

    if (isBusinessDay(year, month, day)) {
      return chicagoWallTimeToDate({ year, month, day });
    }
  }

  return chicagoWallTimeToDate({
    year: chicago.year,
    month: chicago.month,
    day: chicago.day + 1,
  });
};

/**
 * @param {ShippingCutoffLabels} labels
 * @param {Date} [now]
 * @returns {string}
 */
export const getShippingCutoffCopy = (labels, now = new Date()) => {
  const countdown = getTimeUntilCutoff(now);

  if (countdown) {
    return applyCountdownTemplate(labels.shipsToday || '', countdown.hours, countdown.minutes);
  }

  const chicago = getChicagoDateTime(now);
  if (isTomorrowBusinessDay(chicago)) return labels.shipsTomorrow || '';

  return labels.nextBusinessDay || '';
};

/**
 * @param {Date} [now]
 * @returns {Date}
 */
const getNextRenderAt = (now = new Date()) => {
  const countdown = getTimeUntilCutoff(now);
  if (countdown) return countdown.nextTickAt;

  return getNextUpdateAt(getChicagoDateTime(now));
};

/**
 * @param {() => void} onUpdate
 * @returns {() => void}
 */
export const scheduleShippingCutoffUpdate = (onUpdate) => {
  let timeoutId;

  const arm = () => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }

    const delay = Math.max(getNextRenderAt().getTime() - Date.now() + 250, 0);
    timeoutId = window.setTimeout(() => {
      timeoutId = undefined;
      onUpdate();
      arm();
    }, delay);
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    onUpdate();
    arm();
  };

  arm();
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutId = undefined;
    }

    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

class ShinzoShippingCutoff extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #stopUpdates;

  connectedCallback() {
    this.#render();
    requestAnimationFrame(() => this.#render());
    this.#stopUpdates = scheduleShippingCutoffUpdate(() => this.#render());
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.#stopUpdates?.();
    this.#stopUpdates = undefined;
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  #handleCartUpdate = () => {
    requestAnimationFrame(() => this.#render());
  };

  #render() {
    const message = this.querySelector('.shinzo-shipping-cutoff__text');
    if (!(message instanceof HTMLElement)) return;

    const nextText = getShippingCutoffCopy({
      shipsToday: this.dataset.shipsToday,
      shipsTomorrow: this.dataset.shipsTomorrow,
      nextBusinessDay: this.dataset.nextBusinessDay,
    });

    if (nextText) {
      message.textContent = nextText;
    }

    this.classList.add('is-ready');
  }
}

if (!customElements.get('shinzo-shipping-cutoff')) {
  customElements.define('shinzo-shipping-cutoff', ShinzoShippingCutoff);
}
